<template>
  <div>
    <page-header :title="title" :breadcrumb="breadcrumb" :container-class="containerClass" />
    <div class="page-section">
      <div :class="containerClass">
        <page-separator :title="title" />

        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <!-- Search -->
              <div class="col-lg-auto">
                <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent="">
                  <b-form-input
                    class="w-lg-auto"
                    placeholder="Search by exam"
                    v-model="searchTerm"
                    @input="onSearch"
                  />
                  <b-btn variant="flush" type="submit">
                    <md-icon v-text="'search'" />
                  </b-btn>
                </form>
              </div>

              <div class="col-lg d-flex flex-wrap justify-content-end">
                <b-btn :to="{ name: 'student-add-exam-result' }" exact variant="primary"> Add Exam Result </b-btn>
              </div>
            </div>
          </div>

          <!-- Exam Results Table -->
          <b-table
            :fields="tableColumns"
            :items="examResults"
            :busy="isLoading"
            head-variant="light"
            class="table-nowrap"
            hover
            responsive
            no-local-sorting
            @sort-changed="onSortChange"
          >
            <template #cell(test_provider)="data">
              {{ get(data.item, 'test_provider.name', '—') }}
            </template>

            <template #cell(theory)="data">
              <template v-if="data.item.theory_result">
                <b-badge
                  variant="none"
                  :class="`border border-${data.item.theory_result === 'pass' ? 'success' : 'danger'} text-${
                    data.item.theory_result === 'pass' ? 'success' : 'danger'
                  }`"
                  >{{ data.item.theory_result === 'pass' ? 'Pass' : 'Fail' }}</b-badge
                >
                <small v-if="data.item.theory_date" class="text-muted ml-1">{{ data.item.theory_date }}</small>
              </template>
              <span v-else>—</span>
            </template>

            <template #cell(skill)="data">
              <template v-if="data.item.skill_result">
                <b-badge
                  variant="none"
                  :class="`border border-${data.item.skill_result === 'pass' ? 'success' : 'danger'} text-${
                    data.item.skill_result === 'pass' ? 'success' : 'danger'
                  }`"
                  >{{ data.item.skill_result === 'pass' ? 'Pass' : 'Fail' }}</b-badge
                >
                <small v-if="data.item.skill_date" class="text-muted ml-1">{{ data.item.skill_date }}</small>
              </template>
              <span v-else>—</span>
            </template>

            <template #cell(overall_status)="data">
              <b-badge
                :class="`border border-${statusVariant(data.item.overall_status)} text-${statusVariant(
                  data.item.overall_status
                )}`"
                variant="none"
                >{{ statusLabel(data.item.overall_status) }}</b-badge
              >
            </template>

            <template #cell(documents)="data">
              <b-badge variant="none" class="border border-secondary text-secondary">
                {{ (data.item.documents || []).length }}
              </b-badge>
            </template>

            <template #head(actions)="">
              <span></span>
            </template>
            <template #cell(actions)="data">
              <b-dropdown variant="link" toggle-class="text-decoration-none p-0" no-caret>
                <template #button-content>
                  <i class="material-icons icon-24pt text-info">more_vert</i>
                </template>
                <b-dropdown-item
                  :to="{
                    name: 'student-edit-exam-result',
                    params: { id: data.item.id },
                  }"
                >
                  <md-icon class="text-info mr-2">edit</md-icon>
                  {{ $t('generalMsgs.edit') }}
                </b-dropdown-item>
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item @click="removeExamResult(data.item.id)">
                  <md-icon class="text-danger mr-2">delete</md-icon>
                  {{ $t('generalMsgs.remove') }}
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>

          <!-- Footer Pagination -->
          <div class="card-footer">
            <pagination
              v-model="currentPage"
              :total-rows="totalExamResults"
              :per-page="perPage"
              @change="onPageChange"
              aria-controls="exam-results-table"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import { debounce, get } from 'lodash';
import { mapActions } from 'vuex';
import Page from '@/components/Page.vue';
import Pagination from '../../../components/Ui/Pagination.vue';
import MdIcon from '../../../components/Ui/MdIcon.vue';
import { DEFAULT_PAGE_SIZE } from '../../../common/constants';
import PageHeader from '@/components/Ui/PageHeader.vue';

const STATUS_META = {
  not_started: { label: 'Not Started', variant: 'secondary' },
  in_progress: { label: 'In Progress', variant: 'warning' },
  passed: { label: 'Passed', variant: 'success' },
  failed: { label: 'Failed', variant: 'danger' },
};

export default {
  components: {
    MdIcon,
    PageHeader,
    PageSeparator,
    Pagination,
  },
  extends: Page,

  data() {
    return {
      title: 'Exam Results',
      searchTerm: '',
      isLoading: false,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalExamResults: 0,
      ordering: null,
      examResults: [],
    };
  },

  computed: {
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Exam Results', active: true },
      ];
    },

    tableColumns() {
      return [
        {
          key: 'actions',
          label: '',
          class: 'text-left',
          thStyle: { minWidth: '90px' },
        },
        { key: 'test_provider', label: 'Exam' },
        { key: 'theory', label: 'Theory' },
        { key: 'skill', label: 'Skill' },
        { key: 'overall_status', label: 'Overall', sortable: true },
        { key: 'documents', label: 'Docs' },
        { key: 'submitted_at', label: 'Submitted', sortable: true },
      ];
    },
  },

  methods: {
    ...mapActions('examResult', ['getAllExamResults', 'deleteExamResult']),
    get,

    statusVariant(status) {
      return get(STATUS_META, `${status}.variant`, 'secondary');
    },
    statusLabel(status) {
      return get(STATUS_META, `${status}.label`, status);
    },

    async removeExamResult(id) {
      this.isLoading = true;
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm(
          `Please confirm that you want to delete the selected exam result.`,
          {
            title: 'Are you sure?',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Yes',
            cancelTitle: 'No',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          }
        );
        if (isConfirmed) {
          await this.deleteExamResult(id);
          this.fetchExamResults();
          this.makeToast({ variant: 'success', msg: 'Exam result deleted successfully!' });
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    async fetchExamResults(pageNum = 1, params = {}) {
      this.isLoading = true;
      document.getElementById('app').scrollIntoView();

      const response = await this.getAllExamResults({
        limit: this.perPage,
        offset: (pageNum - 1) * this.perPage,
        ...(this.ordering && { ordering: this.ordering }),
        ...(this.searchTerm && { search: this.searchTerm }),
        ...params,
      });
      this.examResults = response.data.results;
      this.currentPage = pageNum;
      this.totalExamResults = response.data.count;
      this.isLoading = false;
    },

    onPageChange(pageNum) {
      this.fetchExamResults(pageNum);
    },

    onSortChange(context) {
      this.ordering = context.sortDesc ? '-' + context.sortBy : context.sortBy;
      this.fetchExamResults();
    },

    onSearch() {
      this.debouncedSearchExamResults(this);
    },

    debouncedSearchExamResults: debounce((vm) => {
      vm.fetchExamResults();
    }, 500),
  },

  async mounted() {
    await this.fetchExamResults();
  },
};
</script>
